import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

// 🛒 TIPOS PARA EL CARRITO
export interface CartItem {
  id: string;
  title: string;
  description: string;
  price: number;
  quantity?: number;
  originalPrice?: number;
  discountPercent?: number;
  currency: string;
  category: string;
  difficulty: string;
  duration: number;
  instructor: string;
  image?: string;
  videoUrl?: string;
  benefits: string[];
  requirements: string[];
  exercises: string[];
  eventDate?: string;
  venue?: string;
  ticketTierName?: string;
  eventId?: string;
  ticketTierId?: string;
  serviceFeeRate?: number;
  organizerId?: string; // ID del organizador para Mercado Pago vinculado
  courseId?: string; // ID del evento publicado
  canReceivePayments?: boolean;
}

export interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
}

export interface AddToCartResult {
  success: boolean;
  error?: string;
}

export interface CartContextType {
  state: CartState;
  addToCart: (item: CartItem) => AddToCartResult;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  replaceCartItems: (items: CartItem[]) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
}

// 🎯 ACCIONES DEL CARRITO
type CartAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'CLEAR_CART' }
  | { type: 'HYDRATE_CART'; payload: CartItem[] }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } };

// 📊 ESTADO INICIAL
const initialState: CartState = {
  items: [],
  total: 0,
  itemCount: 0,
};

const CART_STORAGE_KEY = 'peekpass-cart';

const normalizeCartItem = (item: CartItem): CartItem => {
  const quantity = item.quantity && item.quantity > 0 ? item.quantity : 1;
  const originalPrice = typeof item.originalPrice === 'number' ? item.originalPrice : item.price;
  const discountPercent = typeof item.discountPercent === 'number' ? item.discountPercent : 0;

  return {
    ...item,
    quantity,
    originalPrice,
    discountPercent,
  };
};

const buildCartState = (items: CartItem[]): CartState => {
  const normalizedItems = items.map(normalizeCartItem);
  const total = normalizedItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const itemCount = normalizedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return {
    items: normalizedItems,
    total,
    itemCount,
  };
};

const getOrganizerId = (item?: CartItem) => String(item?.organizerId || '').trim();

// 🔄 REDUCER DEL CARRITO
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const incomingItem = normalizeCartItem(action.payload);
      const existingItem = state.items.find(item => item.id === action.payload.id);

      if (existingItem) {
        const updatedItems = state.items.map((item) =>
          item.id === incomingItem.id
            ? { ...item, quantity: (item.quantity || 1) + (incomingItem.quantity || 1) }
            : item
        );

        return buildCartState(updatedItems);
      }

      return buildCartState([...state.items, incomingItem]);
    }

    case 'REMOVE_FROM_CART': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return buildCartState(newItems);
    }

    case 'CLEAR_CART': {
      return initialState;
    }

    case 'HYDRATE_CART': {
      return buildCartState(action.payload);
    }

    case 'UPDATE_QUANTITY': {
      const normalizedQuantity = Math.max(1, action.payload.quantity);
      const updatedItems = state.items.map((item) =>
        item.id === action.payload.itemId
          ? { ...item, quantity: normalizedQuantity }
          : item
      );

      return buildCartState(updatedItems);
    }

    default:
      return state;
  }
};

// 🏪 CONTEXTO DEL CARRITO
const CartContext = createContext<CartContextType | undefined>(undefined);

// 🎁 PROVEEDOR DEL CARRITO
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // 💾 CARGAR CARRITO DESDE LOCALSTORAGE AL INICIALIZAR
  useEffect(() => {
    const savedCart = loadCartFromStorage();
    if (savedCart.items.length > 0) {
      dispatch({ type: 'HYDRATE_CART', payload: savedCart.items });
    }
  }, []);

  // 💾 GUARDAR CARRITO EN LOCALSTORAGE CUANDO CAMBIE
  useEffect(() => {
    if (state.items.length > 0) {
      saveCartToStorage(state);
      return;
    }

    clearCartFromStorage();
  }, [state]);

  const addToCart = (item: CartItem): AddToCartResult => {
    const currentOrganizerId = getOrganizerId(state.items.find((cartItem) => getOrganizerId(cartItem)));
    const incomingOrganizerId = getOrganizerId(item);

    if (currentOrganizerId && incomingOrganizerId && currentOrganizerId !== incomingOrganizerId) {
      return {
        success: false,
        error: 'No podés mezclar entradas de distintos organizadores en una misma compra. Para continuar con este evento, primero vaciá el carrito actual.'
      };
    }

    dispatch({ type: 'ADD_TO_CART', payload: item });
    return { success: true };
  };

  const removeFromCart = (itemId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: itemId });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const replaceCartItems = (items: CartItem[]) => {
    dispatch({ type: 'HYDRATE_CART', payload: items });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });
  };

  const value: CartContextType = {
    state,
    addToCart,
    removeFromCart,
    clearCart,
    replaceCartItems,
    updateQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// 🎯 HOOK PARA USAR EL CARRITO
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// 💾 PERSISTENCIA EN LOCALSTORAGE
export const saveCartToStorage = (cart: CartState) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

export const clearCartFromStorage = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing cart from localStorage:', error);
  }
};

export const loadCartFromStorage = (): CartState => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart) as CartState;
      return buildCartState(parsedCart.items || []);
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return initialState;
};
